import { useState } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription } from './ui/alert';
import { useForm } from "react-hook-form";
import { ParticleAnimation } from './ParticleAnimation';
import { toast } from "sonner";
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Send, 
  MessageSquare,
  Globe,
  Linkedin,
  Twitter,
  Github,
  CheckCircle,
  Loader2
} from 'lucide-react';

interface ContactFormData {
  first_name: string;
  last_name: string;
  email: string;
  company?: string;
  budget_range?: string;
  message: string;
  phone?: string;
  service_type?: string;
}

// Mock submission function for when API is unavailable
const mockSubmitContact = async (data: ContactFormData): Promise<void> => {
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 1500));
  
  // Log the submission (in a real app, you might store it locally or send to analytics)
  console.log('Mock contact form submission:', data);
  
  // Always succeed for demo purposes
  return Promise.resolve();
};

export function ContactSection() {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  
  const { 
    register, 
    handleSubmit, 
    formState: { errors }, 
    reset 
  } = useForm<ContactFormData>();

  const onSubmit = async (data: ContactFormData) => {
    try {
      setSubmitting(true);
      setError(null);
      setSuccess(false);
      
      // Try to submit via mock function (replace with real API when available)
      await mockSubmitContact(data);
      
      setSuccess(true);
      setShowSuccess(true);
      toast.success("Message sent successfully! We'll get back to you soon.", {
        description: "Thank you for contacting Diroi.",
      });
      reset();
      
      // Hide success message after 5 seconds
      setTimeout(() => setShowSuccess(false), 5000);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message. Please try again.';
      setError(errorMessage);
      toast.error("Failed to send message. Please try again.", {
        description: "There was an error submitting your form.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Headquarters',
      details: ['PT.Digital Robotik Indonesia', 'Magna Commercial, MB.58', 'Summarecon Bandung'],
      color: 'primary'
    },
    {
      icon: Phone,
      title: 'Phone',
      details: [
        {
          label: 'Telepon',
          value: '(022) 753 9899',
          href: 'tel:0227539899',
          icon: Phone
        },
        {
          label: 'WhatsApp',
          value: '62878 4236 1528',
          href: 'https://wa.me/6287842361528',
          icon: null // will use inline SVG
        }
      ],
      color: 'secondary',
      isPhone: true
    },
    {
      icon: Mail,
      title: 'Email Contact',
      details: ['info@digitalrobindo.co.id'],
      color: 'accent'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: ['Monday - Friday: 08.00AM - 17.00PM', 'Saturday: Closed', 'Sunday: Closed'],
      color: 'primary'
    }
  ];

  // const offices = [
  //   { city: 'San Francisco', country: 'USA', status: 'Headquarters' },
  //   { city: 'New York', country: 'USA', status: 'Regional Office' },
  //   { city: 'London', country: 'UK', status: 'European Hub' },
  //   { city: 'Tokyo', country: 'Japan', status: 'Asian Operations' },
  //   { city: 'Sydney', country: 'Australia', status: 'Pacific Office' }
  // ];

  const socialLinks = [
    { icon: Linkedin, label: 'LinkedIn', href: '#', color: 'primary' },
    { icon: Twitter, label: 'Twitter', href: '#', color: 'secondary' },
    { icon: Github, label: 'GitHub', href: '#', color: 'accent' },
    // { icon: Globe, label: 'Blog', href: '#', color: 'primary' }
  ];

  const serviceTypes = [
    'AI & Machine Learning',
    'Cloud Infrastructure', 
    'Mobile Development',
    'Cybersecurity',
    'Data Analytics',
    'UI/UX Design',
    'Digital Transformation',
    'Other'
  ];

  const budgetRanges = [
    '$5k - $15k',
    '$15k - $30k', 
    '$30k - $60k',
    '$60k - $100k',
    '$100k+'
  ];

  return (
    <section id="contact" className="py-20 relative bg-muted/5 overflow-hidden">
      {/* Particle Animation Background */}
      <ParticleAnimation 
        className="opacity-30"
        particleCount={40}
        colors={['#FFD700', '#00FFFF', '#00AEEF']}
        speed={0.6}
        connectionDistance={100}
        showConnections={true}
        particleTypes={['dot', 'circuit']}
      />
      
      <div className="absolute inset-0 digital-grid opacity-10"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-primary/20 text-primary border-primary/30 font-orbitron">
            Contact Us
          </Badge>
          <h2 className="text-4xl md:text-5xl font-orbitron font-bold text-primary mb-6">
            Let's Start Something Amazing
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-primary to-accent mx-auto mb-8"></div>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Ready to transform your business with cutting-edge technology? 
            Get in touch with our team of experts.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card className="p-8 bg-card/30 backdrop-blur border-primary/20 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent"></div>
            
            <div className="relative z-10">
              <div className="flex items-center space-x-3 mb-6">
                <MessageSquare className="w-6 h-6 text-primary" />
                <h3 className="text-2xl font-orbitron font-semibold text-primary">
                  Send us a Message
                </h3>
              </div>
              
              {/* Success Message */}
              {(showSuccess || success) && (
                <Alert className="mb-6 border-green-500/20 bg-green-500/10">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <AlertDescription className="text-green-400">
                    Thank you! Your message has been sent successfully. We'll get back to you within 24 hours.
                  </AlertDescription>
                </Alert>
              )}
              
              {/* Error Message */}
              {error && (
                <Alert className="mb-6 border-destructive/20 bg-destructive/10">
                  <AlertDescription className="text-destructive">
                    {error}
                  </AlertDescription>
                </Alert>
              )}
              
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      First Name *
                    </label>
                    <Input 
                      {...register('first_name', { required: 'First name is required' })}
                      placeholder="John" 
                      className="bg-input-background border-primary/20 focus:border-primary"
                      disabled={submitting}
                    />
                    {errors.first_name && (
                      <p className="text-destructive text-sm mt-1">{errors.first_name.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Last Name *
                    </label>
                    <Input 
                      {...register('last_name', { required: 'Last name is required' })}
                      placeholder="Doe" 
                      className="bg-input-background border-primary/20 focus:border-primary"
                      disabled={submitting}
                    />
                    {errors.last_name && (
                      <p className="text-destructive text-sm mt-1">{errors.last_name.message}</p>
                    )}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Email Address *
                  </label>
                  <Input 
                    type="email" 
                    {...register('email', { 
                      required: 'Email is required',
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: 'Please enter a valid email address'
                      }
                    })}
                    placeholder="john@example.com" 
                    className="bg-input-background border-primary/20 focus:border-primary"
                    disabled={submitting}
                  />
                  {errors.email && (
                    <p className="text-destructive text-sm mt-1">{errors.email.message}</p>
                  )}
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Company
                    </label>
                    <Input 
                      {...register('company')}
                      placeholder="Your Company Name" 
                      className="bg-input-background border-primary/20 focus:border-primary"
                      disabled={submitting}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Phone (Optional)
                    </label>
                    <Input 
                      {...register('phone')}
                      placeholder="+62 898 562 4688" 
                      className="bg-input-background border-primary/20 focus:border-primary"
                      disabled={submitting}
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Service Type
                    </label>
                    <select 
                      {...register('service_type')}
                      className="w-full p-3 bg-input-background border border-primary/20 rounded-lg focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      disabled={submitting}
                    >
                      <option value="">Select Service</option>
                      {serviceTypes.map(service => (
                        <option key={service} value={service}>{service}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Project Budget
                    </label>
                    <select 
                      {...register('budget_range')}
                      className="w-full p-3 bg-input-background border border-primary/20 rounded-lg focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      disabled={submitting}
                    >
                      <option value="">Select Budget Range</option>
                      {budgetRanges.map(range => (
                        <option key={range} value={range}>{range}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Message *
                  </label>
                  <Textarea 
                    {...register('message', { required: 'Message is required' })}
                    placeholder="Tell us about your project requirements..."
                    rows={4}
                    className="bg-input-background border-primary/20 focus:border-primary resize-none"
                    disabled={submitting}
                  />
                  {errors.message && (
                    <p className="text-destructive text-sm mt-1">{errors.message.message}</p>
                  )}
                </div>
                
                <Button 
                  type="submit" 
                  size="lg" 
                  disabled={submitting}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 tech-glow font-orbitron relative overflow-hidden group"
                >
                  <span className="relative z-10 flex items-center">
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                        Sending Message...
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-accent/20 to-secondary/20 transform translate-x-full group-hover:translate-x-0 transition-transform duration-500"></div>
                </Button>
                
                <p className="text-xs text-muted-foreground text-center">
                  We'll get back to you within 24 hours. Your information is secure and confidential.
                </p>
              </form>
            </div>
          </Card>
          
          {/* Contact Information */}
          <div className="space-y-8">
            {/* Contact Details */}
            <div className="grid gap-6">
              {contactInfo.map((info, index) => {
                const Icon = info.icon;
                if (info.isPhone) {
                  return (
                    <Card 
                      key={index} 
                      className={`p-6 bg-card/30 backdrop-blur border-${info.color}/20 hover:border-${info.color}/40 transition-all duration-300 hover:tech-glow hover:-translate-y-1`}
                    >
                      <div className="flex items-start space-x-4">
                        <div className={`p-3 bg-${info.color}/10 rounded-xl`}>
                          <Icon className={`w-6 h-6 text-${info.color}`} />
                        </div>
                        <div>
                          <h4 className={`font-orbitron font-semibold text-${info.color} mb-2`}>
                            {info.title}
                          </h4>
                          {Array.isArray(info.details) && info.details.map((detail, detailIndex) => (
                            <div key={detailIndex} className="flex items-center space-x-2 mb-1">
                              {detail.icon === Phone && (
                                <Phone className="w-4 h-4 text-secondary" />
                              )}
                              {detail.icon === null && (
                                <span className="inline-block w-4 h-4 text-green-500" title="WhatsApp">
                                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.472-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.149-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.151-.174.2-.298.3-.497.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.5-.669-.51-.173-.008-.372-.01-.571-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.1 3.2 5.077 4.363.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.288.173-1.413-.074-.124-.272-.198-.57-.347zm-5.421 6.403h-.001a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.999-3.648-.235-.374A9.86 9.86 0 012.1 12.045C2.111 6.477 6.58 2 12.153 2c2.637 0 5.112 1.027 6.988 2.893A9.825 9.825 0 0122.3 12.07c-.003 5.572-4.471 10.049-9.949 10.049zm8.413-18.462A11.815 11.815 0 0012.153 0C5.453 0 0 5.373 0 12.045a11.96 11.96 0 001.671 6.1L.057 23.925a1.003 1.003 0 00.991 1.26c.08 0 .16-.01.24-.03l6.396-1.68a11.89 11.89 0 004.469.854h.005c6.7 0 12.153-5.373 12.153-12.045a11.89 11.89 0 00-3.482-8.382z"/></svg>
                                </span>
                              )}
                              <a
                                href={detail.href}
                                target={detail.icon === null ? "_blank" : undefined}
                                rel={detail.icon === null ? "noopener noreferrer" : undefined}
                                className="text-muted-foreground text-sm hover:underline"
                              >
                                {detail.value}
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    </Card>
                  );
                }
                return (
                  <Card 
                    key={index} 
                    className={`p-6 bg-card/30 backdrop-blur border-${info.color}/20 hover:border-${info.color}/40 transition-all duration-300 hover:tech-glow hover:-translate-y-1`}
                  >
                    <div className="flex items-start space-x-4">
                      <div className={`p-3 bg-${info.color}/10 rounded-xl`}>
                        <Icon className={`w-6 h-6 text-${info.color}`} />
                      </div>
                      <div>
                        <h4 className={`font-orbitron font-semibold text-${info.color} mb-2`}>
                          {info.title}
                        </h4>
                        {Array.isArray(info.details) && info.details.map ? info.details.map((detail: any, detailIndex: number) => (
                          <p key={detailIndex} className="text-muted-foreground text-sm">
                            {detail}
                          </p>
                        )) : null}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
            
            {/* Global Offices */}
            {/* <Card className="p-6 bg-card/30 backdrop-blur border-secondary/20">
              <h4 className="font-orbitron font-semibold text-secondary mb-4">
                Global Presence
              </h4>
              <div className="space-y-3">
                {offices.map((office, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <div>
                      <span className="font-medium text-foreground">{office.city}</span>
                      <span className="text-muted-foreground text-sm ml-2">{office.country}</span>
                    </div>
                    <Badge variant="outline" className="text-xs border-secondary/30 text-secondary">
                      {office.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </Card> */}
            
            {/* Social Media */}
            <Card className="p-6 bg-card/30 backdrop-blur border-accent/20">
              <h4 className="font-orbitron font-semibold text-accent mb-4">
                Follow Us
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {socialLinks.map((social, index) => {
                  const Icon = social.icon;
                  return (
                    <Button
                      key={index}
                      variant="ghost"
                      className={`justify-start hover:bg-${social.color}/10 hover:text-${social.color} transition-colors duration-300`}
                      asChild
                    >
                      <a href={social.href} target="_blank" rel="noopener noreferrer">
                        <Icon className="w-4 h-4 mr-2" />
                        {social.label}
                      </a>
                    </Button>
                  );
                })}
              </div>
            </Card>
          </div>
        </div>
        
        {/* Footer CTA */}
        <div className="text-center mt-16">
          <Card className="p-8 bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent"></div>
            <div className="relative z-10">
              <h3 className="text-2xl font-orbitron font-bold text-primary mb-4">
                Ready to Innovate Together?
              </h3>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                Whether you're a startup looking to disrupt the market or an enterprise 
                seeking digital transformation, we're here to make it happen.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 tech-glow font-orbitron">
                  Schedule a Consultation
                </Button>
                <Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary/10">
                  Download Our Portfolio
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}